<template>
    <div class="mx-auto max-w-screen-2xl h-full grid sm:grid-cols-3 gap-12">
        <div class="col-span-2">
            <h3 class="text-2xl tracking-widest uppercase font-bold text-gray-700 mb-2">Recent Support Tickets</h3>
            <div class="bg-gray-50 shadow overflow-hidden sm:rounded-md border border-gray-200" v-if="tickets?.length > 0">
                <ul role="list" class="divide-y divide-gray-300">
                    <li v-for="ticket in tickets" :key="ticket.id">
                        <a :href="'https://journolink.supportbee.com/tickets/' + ticket.id" target="_blank" class="block bg-white hover:bg-gray-100">
                            <div class="flex items-center px-4 py-4 sm:px-6">
                                <div class="min-w-0 flex-1 flex items-center">
                                    <div class="min-w-0 flex-1 px-4">
                                        <div>
                                            <p class="text-lg text-gray-800 truncate">
                                                {{ ticket.subject }}
                                            </p>
                                            <p class="mt-2 text-sm">
                                                {{ ticket.summary }}
                                            </p>
                                            <p class="mt-2 text-sm">
                                                <span class="font-bold">Last Activity:</span> {{ formatDateShort(ticket.last_activity_at)  }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div class="-mt-px flex divide-x divide-gray-200">
                                    <div class="w-0 flex-1 flex">
                                        <div class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500">
                                            <template v-if="ticket.unanswered">
                                                <XCircleIcon class="h-5 w-5 text-red-400 mr-2" aria-hidden="true" />
                                            </template>
                                            <template v-else>
                                                <CheckCircleIcon class="h-5 w-5 text-green-400 mr-2" aria-hidden="true" />
                                            </template>
                                            Answered?
                                        </div>
                                    </div>
                                    <div class="w-0 flex-1 flex">
                                        <div class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500">
                                            {{ ticket.replies_count }} Replies
                                        </div>
                                    </div>
                                    <div class="w-0 flex-1 flex">
                                        <div class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500">
                                            {{ ticket.comments_count }} Comments
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </a>
                    </li>
                </ul>
            </div>
            <div v-if="tickets?.length === 0">
                <p class="text-sm text-gray-400">Our support provider has no records for this user.</p>
            </div>
        </div>

        <div class="col-span-1">
            <h3 class="text-2xl tracking-widest uppercase font-bold text-gray-700 mb-4">Notes</h3>

            <NoteCreate
                @created="handleCreatedNote"
                :account="props.user.account_id"
            />
            <template v-if="notes">
                <NoteEntry
                    v-for="note in notes"
                    :key="note.id"
                    :note="note"
                    :account="props.user.account_id"
                    @deleted="handleDeletedNote"
                />
            </template>
        </div>
    </div>
</template>

<script>
import {ref, toRef, onMounted} from "vue"
import {CheckCircleIcon, XCircleIcon} from "@heroicons/vue/24/outline"
import {ExclamationCircleIcon} from "@heroicons/vue/24/solid"
import {Switch, SwitchGroup, SwitchLabel} from "@headlessui/vue"
import NoteCreate from "../../Accounts/Notes/NoteCreate";
import NoteEntry from "../../Accounts/Notes/NoteEntry";

export default {
    name: "Support",
    components: {
        CheckCircleIcon,
        XCircleIcon,
        ExclamationCircleIcon,
        SwitchGroup,
        Switch,
        SwitchLabel,
        NoteCreate,
        NoteEntry,
    },
    props: {
        user: {
            type: Object,
            required: true,
        }
    },
    setup(props) {
        const tickets = ref([])
        const notes = ref([])
        const state = toRef(props, 'user')
        const saveSuccessful = ref(false)

        const getSupportTickets = async () => {
            await axios.get(route('users.support.tickets', { user: props.user.id }))
                .then((response) => {
                    tickets.value = response.data.tickets ?? []
                })
        }

        const getNotes = async () => {
            axios.get(route('accounts.notes.list', props.user.account_id))
                .then(response => {
                    notes.value = response.data.data
                })
                .catch(e => {
                    console.error({e})
                })
        }

        const handleCreatedNote = (note) => {
            notes.value.unshift(note)
        }

        const handleDeletedNote = (note) => {
            notes.value = notes.value.filter(n => n.id !== note.id)
        }

        onMounted(() => {
            window.location.hash = 'support'
            getNotes()
            getSupportTickets()
        })

        return {
            props,
            tickets,
            notes,
            state,
            saveSuccessful,
            handleCreatedNote,
            handleDeletedNote
        }
    }
}
</script>

<style scoped>

</style>
